/**
 * A real backend for the portal screen, implementing the same four methods
 * `localProvider` does (auth.js) so `Auth` itself needs no changes — see
 * README "Swapping in a real backend".
 *
 * The token signup/signin return rides inside the same object `Auth`
 * already persists to `battlechess.session` (auth.js:118), so `saveStats`/
 * `saveGame` can read it back from there directly rather than `Auth`
 * threading it through explicitly.
 */

const BASE = import.meta.env.VITE_API_URL;
const SESSION_KEY = 'battlechess.session';

function currentToken() {
  try {
    return JSON.parse(localStorage.getItem(SESSION_KEY))?.token ?? null;
  } catch {
    return null;
  }
}

/* ---------------- backend status, for the portal's LED ---------------- */

let inFlight = 0;
let reachable = null;          // null until the first call settles either way
const listeners = new Set();

const status = () => {
  if (inFlight > 0) return 'busy';
  if (reachable === null) return 'unknown';
  return reachable ? 'online' : 'offline';
};

const emit = () => {
  const now = status();
  for (const fn of listeners) fn(now);
};

/** Subscribes to 'unknown' | 'busy' | 'online' | 'offline'; fires immediately. */
export function onApiStatus(fn) {
  listeners.add(fn);
  fn(status());
  return () => listeners.delete(fn);
}

/** Asks whether the database behind the API answers. Never throws — the point
 * is the status it records on the way through, not the value. */
export async function checkDbHealth() {
  if (!BASE) return;
  try {
    await request('/api/health');
  } catch {
    /* recorded by `request` already */
  }
}

async function request(path, { method = 'GET', body, auth = false } = {}) {
  const headers = { 'content-type': 'application/json' };
  if (auth) {
    const token = currentToken();
    if (token) headers.authorization = `Bearer ${token}`;
  }

  inFlight++;
  emit();
  try {
    const res = await fetch(`${BASE}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined
    });
    // A refusal the server *answered* — a wrong password, a name already
    // taken — still proves it and its database are up. Only a 5xx (which is
    // how a failed query surfaces, /api/health included) or a fetch that
    // never completed at all means otherwise.
    reachable = res.status < 500;
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error ?? `Request failed (${res.status}).`);
    return data;
  } catch (err) {
    if (err instanceof TypeError) reachable = false;   // the fetch itself failed
    throw err;
  } finally {
    inFlight--;
    emit();
  }
}

export const apiProvider = {
  async signUp(name, password) {
    return request('/api/signup', { method: 'POST', body: { name, password } });
  },

  async signIn(name, password) {
    return request('/api/signin', { method: 'POST', body: { name, password } });
  },

  async signOut() {
    /* Stateless JWTs: nothing to revoke server-side. */
  },

  async saveStats(name, stats) {
    await request('/api/stats', { method: 'POST', auth: true, body: { stats } });
  },

  /** Not part of the base provider contract — called only where it exists
   * (main.js feature-detects `auth.provider.saveGame`), so `localProvider`
   * needs no matching no-op. */
  /** `meta` carries the colour the player commanded, the mode, and (online
   * only) the opponent's name — the three things the server cannot recover
   * from a move list but needs in order to award medals. */
  async saveGame(result, moves, meta = {}) {
    return request('/api/games', { method: 'POST', auth: true, body: { result, moves, ...meta } });
  },

  async medals() {
    return request('/api/medals', { auth: true });
  },

  async recentGames() {
    return request('/api/games', { auth: true });
  },

  /** No `auth: true` — the leaderboard is public within the app, same as
   * the API's own `/api/leaderboard` route (no `requireAuth`). */
  async leaderboard() {
    return request('/api/leaderboard');
  }
};
